<script setup lang="ts">
import { onUnmounted, watch } from 'vue';
import { applyConsentUpdate, applyDefaultConsent, ensureGtag } from '@/utils/gtag';

const props = defineProps<{
  gtmId: string | null;
  gtmEnabled: boolean;
  hasConsent: boolean;
}>();

const scriptId = 'gtm-script';
const noScriptId = 'gtm-noscript';
let activeGtmId: string | null = null;
const defaultConsentFlag = '__gtmDefaultApplied';

const ensureGtmStartEvent = () => {
  const win = window as any;
  const dataLayer: Array<Record<string, unknown>> = (win.dataLayer ??= []);
  const hasStartEvent = dataLayer.some((entry) => entry?.event === 'gtm.js');
  if (hasStartEvent) return;
  dataLayer.push({
    'gtm.start': Date.now(),
    event: 'gtm.js',
  });
};

const ensureNoscript = (gtmId: string) => {
  if (document.getElementById(noScriptId)) return;
  const noscript = document.createElement('noscript');
  noscript.id = noScriptId;
  noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
  document.body.appendChild(noscript);
};

const loadGtmScript = (gtmId: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      const cachedState = (existingScript as HTMLScriptElement).getAttribute('data-gtm-loaded');
      if (cachedState === 'true') {
        resolve();
        return;
      }
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Failed loading GTM script')), { once: true });
      return;
    }

    const firstScript = document.getElementsByTagName('script')[0];
    const script = document.createElement('script');
    script.id = scriptId;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    script.addEventListener('load', () => {
      script.setAttribute('data-gtm-loaded', 'true');
      resolve();
    });
    script.addEventListener('error', () => reject(new Error('Failed loading GTM script')));

    ensureGtmStartEvent();
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }

    ensureNoscript(gtmId);
  });
};

const removeGtmScript = () => {
  document.getElementById(scriptId)?.remove();
  document.getElementById(noScriptId)?.remove();
  activeGtmId = null;
};

const applyDefaultConsentOnce = () => {
  if (typeof window === 'undefined') return;
  const win = window as any;
  if (win[defaultConsentFlag]) return;
  applyDefaultConsent();
  win[defaultConsentFlag] = true;
};

applyDefaultConsentOnce();

const setupGtm = async (gtmId: string) => {
  try {
    ensureGtag();
    applyDefaultConsentOnce();
    await loadGtmScript(gtmId);
    if (props.hasConsent) {
      applyConsentUpdate(true);
    }
  } catch (error) {
    console.error('Failed to load GTM', error);
  }
};

watch(
  () => [props.gtmEnabled, props.gtmId] as const,
  async ([enabled, gtmId]) => {
    if (!enabled || !gtmId) {
      if (activeGtmId) {
        applyConsentUpdate(false);
      }
      removeGtmScript();
      return;
    }

    if (activeGtmId === gtmId && document.getElementById(scriptId)) {
      return;
    }

    removeGtmScript();
    await setupGtm(gtmId);
    activeGtmId = gtmId;
  },
  { immediate: true },
);

watch(
  () => props.hasConsent,
  (isGranted) => {
    if (!props.gtmEnabled || !props.gtmId) return;
    ensureGtag();
    applyConsentUpdate(Boolean(isGranted));
  },
);

onUnmounted(() => {
  removeGtmScript();
});
</script>

<template>
  <div></div>
</template>
